import React, { useEffect, useState } from 'react';
import Widget from '../Widget';
import { TopicRadarData, WIDGET_KEYS } from '../../../../types/Widgets';
import { TOPIC_RADAR_FAKE_DATA } from '../../../../utils/fakeData';
import {
  useIsSingleExamScope,
  useScopedPrimaryExam,
  useScopedStatsFetchers,
  useScopedToggleStore,
} from '../performanceScope';
import ExamGlobalToggle from '../../Global/ExamGlobalToggle';
import type { TopicTreeView } from '../../../../services/topics';
import TopicRadarChart from './TopicRadarChart';

interface TopicRadarProps {
  isDemo?: boolean;
  showDelete?: boolean;
  view?: 'exam' | 'global';
  onViewChange?: (value: 'exam' | 'global') => void;
  showToggle?: boolean;
  topicView?: TopicTreeView;
}

const TopicRadar: React.FC<TopicRadarProps> = ({
  isDemo = false,
  showDelete = false,
  view,
  onViewChange,
  showToggle = true,
  topicView,
}) => {
  const primaryExam = useScopedPrimaryExam();
  const { fetchTopicPerformance } = useScopedStatsFetchers();
  const toggleStore = useScopedToggleStore();
  const toggleStorageKey = 'ora-topic-radar-toggle';

  const [internalView, setInternalView] = useState<'exam' | 'global'>(() => {
    const saved = toggleStore.get(toggleStorageKey);
    if (saved === 'exam' || saved === 'global') return saved;
    return 'exam';
  });

  const isExamDisabled = !primaryExam?.id;
  // A single-exam (NCLEX-RN) user has no global scope: neither a persisted
  // 'global' nor one passed in by the host ever applies.
  const isSingleExamScope = useIsSingleExamScope();
  const resolvedView = isSingleExamScope ? 'exam' : (view ?? internalView);

  const [topicPerformance, setTopicPerformance] = useState<
    TopicRadarData | null | undefined
  >(null);

  useEffect(() => {
    if (isDemo) {
      setTopicPerformance(TOPIC_RADAR_FAKE_DATA);
    } else {
      void getTopicStats();
    }
  }, [isDemo, primaryExam?.id, resolvedView, topicView, fetchTopicPerformance]);

  useEffect(() => {
    if (isExamDisabled && resolvedView === 'exam') {
      if (onViewChange) {
        onViewChange('global');
      } else {
        setInternalView('global');
      }
    }
  }, [isExamDisabled, resolvedView, onViewChange]);

  const getTopicStats = async () => {
    if (resolvedView === 'exam' && !primaryExam?.id) return;

    try {
      const res = await fetchTopicPerformance(
        resolvedView === 'exam' ? primaryExam?.id : undefined,
        topicView
      );

      if (!res?.topics) return;

      const formattedStats = res.topics.reduce(
        (
          acc: TopicRadarData,
          topic: {
            name: string;
            correct: number;
            total: number;
            percent: number;
          }
        ) => {
          acc[topic.name] = {
            correct: topic.correct,
            total: topic.total,
            percentage: topic.percent,
          };

          return acc;
        },
        {} as TopicRadarData
      );

      setTopicPerformance(formattedStats);
    } catch (error) {
      console.error('Failed to fetch topic performance stats', error);
    }
  };

  return (
    <Widget
      widgetKey={WIDGET_KEYS.TOPIC_RADAR}
      name={'topic-radar'}
      title="Topic Performance"
      subtitle={
        resolvedView === 'exam'
          ? `Topic performance for ${primaryExam?.name ?? 'exam'}`
          : 'Topic performance across all exams'
      }
      headerActions={
        showToggle ? (
          <ExamGlobalToggle
            scopeLabel="Topic Performance scope"
            value={resolvedView}
            onChange={(nextValue) => {
              if (onViewChange) {
                onViewChange(nextValue);
                return;
              }
              setInternalView(nextValue);
              toggleStore.set(toggleStorageKey, nextValue);
            }}
          />
        ) : undefined
      }
      showDelete={showDelete}
    >
      <TopicRadarChart data={topicPerformance} />
    </Widget>
  );
};

export default TopicRadar;
